/**
 * Icon set.
 *
 * Hand-written 24x24 paths on a single grid rather than an icon package: the
 * board only needs about thirty glyphs, and keeping them here means no runtime
 * dependency and no font loading flash in the toolbar.
 */
export type IconName =
  | 'cursor'
  | 'hand'
  | 'sticky'
  | 'square'
  | 'circle'
  | 'triangle'
  | 'diamond'
  | 'text'
  | 'pen'
  | 'marker'
  | 'highlighter'
  | 'eraser'
  | 'arrow'
  | 'frame'
  | 'comment'
  | 'laser'
  | 'image'
  | 'notebook'
  | 'undo'
  | 'redo'
  | 'plus'
  | 'minus'
  | 'fit'
  | 'lock'
  | 'unlock'
  | 'trash'
  | 'copy'
  | 'group'
  | 'ungroup'
  | 'toFront'
  | 'toBack'
  | 'alignLeft'
  | 'alignCenterH'
  | 'alignRight'
  | 'alignTop'
  | 'alignCenterV'
  | 'alignBottom'
  | 'distributeH'
  | 'distributeV'
  | 'grid'
  | 'map'
  | 'download'
  | 'share'
  | 'more'
  | 'chevronDown'
  | 'check'
  | 'close'
  | 'mic'
  | 'micOff'
  | 'video'
  | 'videoOff'
  | 'flipCamera'
  | 'phone'
  | 'record'
  | 'users'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'pin'
  | 'link'
  | 'arrowLeft'
  | 'calendar'
  | 'org'
  | 'bell'
  | 'clock'
  | 'pencil'
  | 'boards'
  | 'magic'
  | 'play'
  | 'template'
  | 'layers'
  | 'search'
  | 'history'
  | 'timer'
  | 'vote'
  | 'maximize'
  | 'minimize'
  | 'eye'
  | 'eyeOff'
  | 'sitemap'
  | 'list'
  | 'print'
  | 'shield'
  | 'logout'
  | 'graduation'
  | 'clipboard'
  | 'paperclip'
  | 'wallet'
  | 'refresh'
  | 'folder'
  | 'popout'
  | 'popin'
  | 'send'
  | 'crop'
  | 'chevronLeft'
  | 'chevronRight'
  | 'sun'
  | 'moon';

const PATHS: Record<IconName, string> = {
  cursor: 'M5 3l14 8-6 1.6L9.6 20z',
  hand: 'M8 12V5.5a1.5 1.5 0 013 0V11m0-1.5a1.5 1.5 0 013 0V12m0-1a1.5 1.5 0 013 0v5a5 5 0 01-5 5h-2a5 5 0 01-5-5v-3l-1.4-1.4a1.5 1.5 0 012.1-2.1L8 12',
  sticky: 'M5 4h14v10l-5 5H5z M14 19v-5h5',
  square: 'M4 5h16v14H4z',
  circle: 'M12 4a8 7.5 0 100 15 8 7.5 0 000-15z',
  triangle: 'M12 4l8 15H4z',
  diamond: 'M12 3l8 9-8 9-8-9z',
  text: 'M5 6h14M12 6v13M9 19h6',
  /*
   * The four drawing tools share one construction: a body on the 45° diagonal
   * from bottom-left to top-right, so at 16px they read as a family and differ
   * only where it matters — the tip. Pen: slim barrel, collared nib with a
   * slit and breather hole. Marker: wider body, a collar, and a slanted chisel
   * cut. Highlighter: stubby, widest of all, with a flat tip the full width of
   * the body. Eraser: a rounded block with its band, resting over the mark it
   * is rubbing out.
   */
  pen: 'M4.5 19.5L7.8 13.6 7.9 12.7 15.5 5.1a2.4 2.4 0 013.4 3.4L11.3 16.1 10.4 16.2z M5.3 18.7l2.2-2.2 M8.1 16h.01',
  marker:
    'M4.3 17.9L7.8 12.2 14.75 5.25a1.2 1.2 0 011.7 0l2.3 2.3a1.2 1.2 0 010 1.7L11.8 16.2 7.5 18.6z M7.8 12.2l4 4',
  highlighter:
    'M3.5 16.8L5.9 13.8 12.7 7.1a1.4 1.4 0 012 0l2.2 2.2a1.4 1.4 0 010 2L10.2 18.1 7.2 20.5z M5.9 13.8l4.3 4.3',
  eraser:
    'M5.1 13.5L12 6.6a1.15 1.15 0 011.6 0l3.8 3.8a1.15 1.15 0 010 1.6l-6.9 6.9a1.15 1.15 0 01-1.6 0l-3.8-3.8a1.15 1.15 0 010-1.6z M7.1 11.5l5.4 5.4 M12.5 21.5h8.5',
  arrow: 'M4 20L20 4M20 4h-7M20 4v7',
  frame: 'M4 8h16M4 16h16M8 4v16M16 4v16',
  notebook: 'M6 4h12a1 1 0 011 1v14a1 1 0 01-1 1H6a2 2 0 01-2-2V6a2 2 0 012-2z M6 4v16 M10 8h6M10 12h6M10 16h4',
  comment: 'M20 6a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2h2v4l4-4h6a2 2 0 002-2z',
  laser:
    'M12 3v3M12 18v3M3 12h3M18 12h3M6 6l2 2M16 16l2 2M18 6l-2 2M8 16l-2 2M12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z',
  image: 'M4 5h16v14H4z M8 11a1.6 1.6 0 100-3.2A1.6 1.6 0 008 11z M4 17l5-5 4 4 3-2.5 4 3.5',
  undo: 'M4 9h9a5 5 0 010 10H8M4 9l4-4M4 9l4 4',
  redo: 'M20 9h-9a5 5 0 000 10h5M20 9l-4-4M20 9l-4 4',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  fit: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
  lock: 'M6 11h12v9H6z M9 11V8a3 3 0 016 0v3',
  unlock: 'M6 11h12v9H6z M9 11V8a3 3 0 015.6-1.5',
  trash: 'M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13M10 11v6M14 11v6',
  copy: 'M8 8h11v11H8z M16 8V5H5v11h3',
  group: 'M4 4h6v6H4z M14 14h6v6h-6z M10 7h4v7h-3',
  ungroup: 'M4 4h6v6H4z M14 14h6v6h-6z',
  toFront: 'M8 4h8v8H8z M4 12h4v8h8v-4',
  toBack: 'M16 12h4v8h-8v-4 M4 4h8v8H4z',
  alignLeft: 'M4 3v18M8 7h9M8 13h6',
  alignCenterH: 'M12 3v18M7 7h10M9 13h6',
  alignRight: 'M20 3v18M7 7h9M10 13h6',
  alignTop: 'M3 4h18M7 8v9M13 8v6',
  alignCenterV: 'M3 12h18M7 7v10M13 9v6',
  alignBottom: 'M3 20h18M7 7v9M13 10v6',
  distributeH: 'M4 3v18M20 3v18M11 7h2v10h-2z',
  distributeV: 'M3 4h18M3 20h18M7 11v2h10v-2z',
  grid: 'M4 4h16v16H4z M4 10h16M4 15h16M10 4v16M15 4v16',
  map: 'M4 6l5-2 6 2 5-2v14l-5 2-6-2-5 2z M9 4v14M15 6v14',
  download: 'M12 4v11M8 11l4 4 4-4M5 19h14',
  share:
    'M8 12l8-4M8 12l8 4M18 5.5a2 2 0 100 4 2 2 0 000-4z M6 10a2 2 0 100 4 2 2 0 000-4z M18 14.5a2 2 0 100 4 2 2 0 000-4z',
  more: 'M6.5 12h.01M12 12h.01M17.5 12h.01',
  chevronDown: 'M6 10l6 5 6-5',
  check: 'M5 13l4 4L19 7',
  close: 'M6 6l12 12M18 6L6 18',
  mic: 'M12 4a3 3 0 013 3v4a3 3 0 01-6 0V7a3 3 0 013-3z M6 11a6 6 0 0012 0M12 17v3M9 20h6',
  micOff:
    'M4 4l16 16M9 6.5A3 3 0 0115 7v4M9 11v0a3 3 0 004.5 2.6M6 11a6 6 0 008.5 5.4M12 17v3M9 20h6',
  video: 'M4 7h11v10H4z M15 11l5-3v8l-5-3z',
  videoOff: 'M4 4l16 16M4 7h9v0M4 7v10h10M15 11l5-3v8',
  flipCamera:
    'M4 7h11v10H4z M15 11l5-3v8l-5-3z M7 3.5A8 8 0 014.5 6M7 3.5V5.5M7 3.5H5 M17 20.5A8 8 0 0019.5 18M17 20.5V18.5M17 20.5H19',
  phone: 'M4 6c0 8 6 14 14 14l2-4-4-2-2 2a12 12 0 01-6-6l2-2-2-4z',
  record: 'M12 4a8 8 0 100 16 8 8 0 000-16z M12 9a3 3 0 100 6 3 3 0 000-6z',
  users:
    'M8 11a3.2 3.2 0 100-6.4A3.2 3.2 0 008 11z M2.5 19a5.5 5.5 0 0111 0 M16 11.5a3 3 0 100-6 M15 14.2a5.5 5.5 0 016.5 4.8',
  bold: 'M7 4h5.5a3.5 3.5 0 010 7H7z M7 11h6.5a4 4 0 010 8H7z',
  italic: 'M10 4h7M7 20h7M14 4l-4 16',
  underline: 'M7 4v7a5 5 0 0010 0V4M5 20h14',
  strike: 'M7 5.5A4.5 4.5 0 0116 7M17 16a4.5 4.5 0 01-9 .5M4 12h16',
  pin: 'M12 3a6 6 0 016 6c0 4.5-6 12-6 12S6 13.5 6 9a6 6 0 016-6z M12 7a2 2 0 100 4 2 2 0 000-4z',
  link: 'M9.5 14.5l5-5M8 12l-2 2a3.5 3.5 0 005 5l2-2M16 12l2-2a3.5 3.5 0 00-5-5l-2 2',
  arrowLeft: 'M19 12H5M5 12l6-6M5 12l6 6',
  calendar: 'M4 7h16v13H4z M4 11h16M8 4v4M16 4v4',
  org: 'M4 20V6l6-2v16M10 20h10V10l-6-2M7 10h.01M7 14h.01M14 13h.01M17 13h.01M14 17h.01M17 17h.01',
  bell: 'M6 9a6 6 0 1112 0c0 4 1.5 6 1.5 6h-15S6 13 6 9z M10 19a2 2 0 004 0',
  clock: 'M12 4a8 8 0 100 16 8 8 0 000-16z M12 8v4.5l3 2',
  pencil: 'M4 20l1-4 11-11 3 3-11 11z M14.5 6.5l3 3',
  boards: 'M4 4h7v7H4z M13 4h7v4h-7z M13 10h7v10h-7z M4 13h7v7H4z',
  magic: 'M5 19L19 5M15 5h4v4M9 15v4H5v-4z M17 3v3M17 9v0.01 M20 6h-3',
  play: 'M6 4l14 8-14 8z',
  template: 'M4 4h7v7H4z M13 4h7v16h-7z M4 13h7v7H4z',
  layers: 'M12 3l9 5-9 5-9-5z M3 13l9 5 9-5M3 17l9 5 9-5',
  search: 'M11 4a7 7 0 100 14 7 7 0 000-14z M21 21l-5.5-5.5',
  history: 'M12 4a8 8 0 100 16 8 8 0 000-16z M12 8v4.5l3 2 M4.5 9H3V6',
  timer: 'M10 2h4M12 4v3M12 22a8 8 0 100-16 8 8 0 000 16z M12 10v4l3 2 M18 6l1.5-1.5',
  vote: 'M6 20V10M12 20V4M18 20v-8',
  maximize:
    'M9 4H5a1 1 0 00-1 1v4M15 4h4a1 1 0 011 1v4M9 20H5a1 1 0 01-1-1v-4M15 20h4a1 1 0 001-1v-4',
  minimize:
    'M4 9V6a1 1 0 011-1h3M20 9V6a1 1 0 00-1-1h-3M4 15v3a1 1 0 001 1h3M20 15v3a1 1 0 01-1 1h-3',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z M12 15a3 3 0 100-6 3 3 0 000 6z',
  eyeOff:
    'M3 3l18 18M10.6 10.6a3 3 0 004.2 4.2M6.1 6.2C3.6 7.9 2 12 2 12s3.5 7 10 7c1.7 0 3.2-.4 4.4-1M9.9 5.1A11 11 0 0112 5c6.5 0 10 7 10 7a15 15 0 01-2.2 3',
  sitemap: 'M4 4h5v5H4z M15 4h5v5h-5z M9.5 14h5v6h-5z M6.5 9v3h11V9M12 14v-2',
  list: 'M4 6h.01M4 12h.01M4 18h.01M9 6h11M9 12h11M9 18h11',
  print:
    'M6 9V3h12v6 M6 18H4a1 1 0 01-1-1v-6a1 1 0 011-1h16a1 1 0 011 1v6a1 1 0 01-1 1h-2 M6 14h12v7H6z',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z M9 12l2 2 4-4',
  logout: 'M9 4H5a1 1 0 00-1 1v14a1 1 0 001 1h4 M15 8l4 4-4 4 M19 12H9',
  graduation: 'M2 8l10-4 10 4-10 4z M6 10.5v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5 M22 8v6',
  clipboard: 'M9 3h6v3H9z M6 5h12v16H6z M9 11h6M9 15h6',
  refresh: 'M20 12a8 8 0 11-2.3-5.7M20 4v4h-4',
  paperclip: 'M8 12.5V6.5a3 3 0 016 0v9a4 4 0 01-8 0v-8',
  wallet:
    'M3 7h16a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z M17 7V5a2 2 0 00-2-2H6a2 2 0 00-2 2v2 M16 13h3',
  folder: 'M3 8a2 2 0 012-2h4l2 2.5h8a2 2 0 012 2V18a2 2 0 01-2 2H5a2 2 0 01-2-2z',
  /** Move this panel out into its own operating-system window. */
  popout:
    'M14 4h6v6 M13 11l7-7 M18 13v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5',
  /** Bring it back into the page. */
  popin: 'M10 20H4v-6 M11 13l-7 7 M6 11V6a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2h-5',
  send: 'M4 11.5L20 4l-7.5 16-2.2-6.3z M10.3 13.7L20 4',
  crop: 'M6 3v13a2 2 0 002 2h13M3 6h13a2 2 0 012 2v13',
  chevronLeft: 'M14 6l-6 6 6 6',
  chevronRight: 'M10 6l6 6-6 6',
  sun: 'M12 4v2M12 18v2M4 12h2M18 12h2M6.2 6.2l1.4 1.4M16.4 16.4l1.4 1.4M17.8 6.2l-1.4 1.4M7.6 16.4l-1.4 1.4M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z',
  moon: 'M19 13.5A7.5 7.5 0 1110.5 5 6 6 0 0019 13.5z',
};

/**
 * Optional second stroke, painted at reduced opacity under the main path: a
 * sheen on the pen barrel, the cap ring on the marker, the mark a highlighter
 * has just laid down, the crumbs an eraser leaves behind. Same colour, so the
 * icon stays one `currentColor` glyph and needs no palette of its own.
 */
const ACCENTS: Partial<Record<IconName, string>> = {
  pen: 'M10.5 12.1l4.3-4.3',
  marker: 'M10.3 9.7l4 4',
  highlighter: 'M10 21h6',
  eraser: 'M3 21.5h2M7.5 21.5h2',
};

/** Opacity of the accent stroke: present, never competing with the silhouette. */
const ACCENT_OPACITY = 0.45;

/** Icons that read better filled than stroked. */
const FILLED = new Set<IconName>(['cursor']);

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 20, className }: IconProps) {
  const filled = FILLED.has(name);
  const accent = ACCENTS[name];
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {accent && (
        <path
          d={accent}
          stroke="currentColor"
          strokeOpacity={ACCENT_OPACITY}
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      <path
        d={PATHS[name]}
        stroke={filled ? 'none' : 'currentColor'}
        fill={filled ? 'currentColor' : 'none'}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
